import { Divider, IconButton, Stack, Typography } from '@mui/material';
import { Provider, type Account } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { AccountIndicator } from '@/components/accounts/accountIndicator';
import ButtonWithIcon from '@/components/buttons/buttonWithIcon';
import CopyToClipboardButton from '@/components/buttons/copyToClipboardButton';
import MainDialog from '@/components/dialogs/mainDialog';
import { VaultColorSelectorWithButtons } from '@/components/vaults/createBackupVaultPanel';
import { KmsCopyablePolicy } from '@/components/vaults/kmsCopyablePolicy';
import { KmsEditKeyPolicyButton } from '@/components/vaults/kmsEditKeyPolicyButton';
import { useBackupVaultColorUpdate } from '@/components/vaults/useBackupVaultColorUpdate';
import { VaultColoredDot } from '@/components/vaults/vaultColoredDot';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { useDAL } from '@/contexts/useDAL';
import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';

import { OwnBucketSuggestedPolicyDialogBody } from './ownBucketSuggestedPolicyDialogBody';

export const VaultDetailsDialog = ({
  isOpen,
  onClose,
  backupVault,
  vaultAccount,
  onSaveSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  backupVault: BackupVault;
  vaultAccount?: Account;
  onSaveSuccess: () => void;
}) => {
  const [isKmsSuggestedPolicyDialogOpen, setIsKmsSuggestedPolicyDialogOpen] =
    useState(false);
  const [
    isOwnBucketSuggestedPolicyDialogOpen,
    setIsOwnBucketSuggestedPolicyDialogOpen,
  ] = useState(false);

  const dialogTitle =
    isKmsSuggestedPolicyDialogOpen || isOwnBucketSuggestedPolicyDialogOpen
      ? 'Get suggested policy'
      : 'Vault Details';

  const dialogSubtitle =
    isKmsSuggestedPolicyDialogOpen || isOwnBucketSuggestedPolicyDialogOpen
      ? 'To restore from your self-managed vault, apply this policy in your AWS account. Without it, restoring can fail.'
      : 'View the vault’s configuration. Only the name and color can be edited.';

  const onBack = () => {
    if (isKmsSuggestedPolicyDialogOpen) {
      setIsKmsSuggestedPolicyDialogOpen(false);
    }
    if (isOwnBucketSuggestedPolicyDialogOpen) {
      setIsOwnBucketSuggestedPolicyDialogOpen(false);
    }
  };

  return (
    <MainDialog
      isOpen={isOpen}
      isLoading={false}
      header={{
        onBack:
          isKmsSuggestedPolicyDialogOpen || isOwnBucketSuggestedPolicyDialogOpen
            ? onBack
            : undefined,
        onClose: onClose,
        title: dialogTitle,
        subtitle: dialogSubtitle,
      }}
    >
      {isKmsSuggestedPolicyDialogOpen ? (
        <KmsSuggestedPolicyDialogBody backupVault={backupVault} />
      ) : isOwnBucketSuggestedPolicyDialogOpen ? (
        <OwnBucketSuggestedPolicyDialogBody backupVault={backupVault} />
      ) : (
        <VaultDetailsDialogBody
          backupVault={backupVault}
          vaultAccount={vaultAccount}
          onSaveSuccess={onSaveSuccess}
          setIsKmsSuggestedPolicyDialogOpen={setIsKmsSuggestedPolicyDialogOpen}
          setIsOwnBucketSuggestedPolicyDialogOpen={
            setIsOwnBucketSuggestedPolicyDialogOpen
          }
        />
      )}
    </MainDialog>
  );
};

const VaultDetailsDialogBody = ({
  backupVault,
  vaultAccount,
  onSaveSuccess,
  setIsKmsSuggestedPolicyDialogOpen,
  setIsOwnBucketSuggestedPolicyDialogOpen,
}: {
  backupVault: BackupVault;
  vaultAccount?: Account;
  onSaveSuccess: () => void;
  setIsKmsSuggestedPolicyDialogOpen: (isOpen: boolean) => void;
  setIsOwnBucketSuggestedPolicyDialogOpen: (isOpen: boolean) => void;
}) => {
  const [vault, setVault] = useState(backupVault);
  const [isEditingColor, setIsEditingColor] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const dal = useDAL();
  const { updateVaultColor } = useBackupVaultColorUpdate();

  const accountLabel =
    vaultAccount?.cloudProvider === Provider.Azure
      ? 'Subscription'
      : vaultAccount?.cloudProvider === Provider.Gcp
        ? 'Project'
        : 'Account';

  return (
    <Stack
      marginBottom='16px'
      gap='16px'
      divider={<Divider sx={{ borderStyle: 'dashed' }} />}
    >
      <RowWrapper>
        <Typography width='150px'>Name</Typography>
        <RenameEntityName
          name={vault.name}
          onSubmit={(name) => {
            const updatedVault = { ...vault, name };
            void dal.vaults.update(updatedVault).then(() => {
              setVault(updatedVault);
              setIsEditingName(false);
              onSaveSuccess();
            });
          }}
          bold={false}
          editMode={isEditingName}
          setEditMode={setIsEditingName}
        />
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>ID</Typography>
        <Stack direction='row' gap='16px' alignItems='center'>
          <Typography>{vault.id}</Typography>
          <CopyToClipboardButton textToCopy={vault.id} />
        </Stack>
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>Color</Typography>
        {isEditingColor ? (
          <VaultColorSelectorWithButtons
            initialData={vault}
            saveData={(vault) => {
              void updateVaultColor(vault).then(() => {
                setVault(vault);
                setIsEditingColor(false);
                onSaveSuccess();
              });
            }}
            onCancel={() => setIsEditingColor(false)}
            disabled={false}
          />
        ) : (
          <Stack alignItems='center' direction='row' gap='12px'>
            <VaultColoredDot color={vault.backgroundColor} />
            <IconButton
              onClick={() => setIsEditingColor(true)}
              data-testid='rename-entity-name-edit-btn'
            >
              <i
                className={
                  'material-symbols-stylus-outline w-[20px] h-[20px] text-textPrimary'
                }
              />
            </IconButton>
          </Stack>
        )}
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>Cloud</Typography>
        <Typography>
          {CloudProviders[vault.vaultAttributes.cloudProvider].displayName}
        </Typography>
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>Region</Typography>
        <Typography>{vault.region}</Typography>
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>Type</Typography>
        <Typography>
          {vaultAccount && !vaultAccount.isCustomerOwned
            ? 'Eon-managed'
            : 'Self-managed'}
        </Typography>
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>{accountLabel}</Typography>
        {vaultAccount ? <AccountIndicator account={vaultAccount} /> : '--'}
      </RowWrapper>
      {vaultAccount?.isCustomerOwned &&
        vault.vaultAttributes.aws?.bucketName && (
          <RowWrapper justifyContent='space-between'>
            <Stack direction='row' gap='16px'>
              <Typography width='150px'>Bucket</Typography>
              <Typography>{vault.vaultAttributes.aws?.bucketName}</Typography>
            </Stack>
            <ButtonWithIcon
              variant='text'
              text='Get Suggested Policy'
              icon='material-symbols-data-object'
              onClick={() => {
                setIsOwnBucketSuggestedPolicyDialogOpen(true);
              }}
            />
          </RowWrapper>
        )}
      {vaultAccount?.isCustomerOwned &&
        vault.vaultAttributes.gcp?.bucketName && (
          <RowWrapper>
            <Stack direction='row' gap='16px'>
              <Typography width='150px'>Bucket</Typography>
              <Typography>{vault.vaultAttributes.gcp?.bucketName}</Typography>
            </Stack>
          </RowWrapper>
        )}
      {vaultAccount?.isCustomerOwned &&
        vault.vaultAttributes.azure?.storageAccount && (
          <RowWrapper>
            <Stack direction='row' gap='16px'>
              <Typography width='150px'>Storage Account</Typography>
              <Typography>
                {vault.vaultAttributes.azure?.storageAccount}
              </Typography>
            </Stack>
          </RowWrapper>
        )}
      {vault.vaultAttributes.aws?.encryptionKey && (
        <RowWrapper justifyContent='space-between'>
          <Stack direction='row' gap='16px'>
            <Typography width='150px'>KMS Key</Typography>
            <Typography>{vault.vaultAttributes.aws?.encryptionKey}</Typography>
          </Stack>
          <ButtonWithIcon
            variant='text'
            text='Get Suggested Policy'
            icon='material-symbols-data-object'
            onClick={() => {
              setIsKmsSuggestedPolicyDialogOpen(true);
            }}
          />
        </RowWrapper>
      )}
    </Stack>
  );
};

const KmsSuggestedPolicyDialogBody = ({
  backupVault,
}: {
  backupVault: BackupVault;
}) => {
  return (
    <Stack paddingX='40px' gap='24px' alignItems='flex-end'>
      <KmsCopyablePolicy backupVault={backupVault} />
      <KmsEditKeyPolicyButton backupVault={backupVault} />
    </Stack>
  );
};

const RowWrapper = ({
  children,
  justifyContent,
}: {
  children: React.ReactNode;
  justifyContent?: string;
}) => {
  return (
    <Stack
      minHeight='32px'
      flexGrow={1}
      paddingRight='24px'
      paddingLeft='40px'
      direction='row'
      gap='16px'
      alignItems='center'
      justifyContent={justifyContent}
    >
      {children}
    </Stack>
  );
};
